import { createContext, useCallback, useContext, useState } from 'react';
import { Snackbar, Alert, Button, Stack } from '@mui/material';

const SnackbarContext = createContext({
  showSnackbar: () => {},
  confirmSnackbar: () => Promise.resolve(false),
});

export function SnackbarProvider({ children }) {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info',
    autoHideDuration: 4000,
    actionLabel: null,
    cancelLabel: null,
    onResolve: null,
  });

  const showSnackbar = useCallback((message, severity = 'info', options = {}) => {
    setSnackbar({
      open: true,
      message,
      severity,
      autoHideDuration: options.autoHideDuration ?? 4000,
      actionLabel: null,
      cancelLabel: null,
      onResolve: null,
    });
  }, []);

  const confirmSnackbar = useCallback((message, options = {}) => {
    return new Promise((resolve) => {
      setSnackbar({
        open: true,
        message,
        severity: options.severity || 'info',
        autoHideDuration: options.autoHideDuration ?? 8000,
        actionLabel: options.actionLabel || 'Bestatigen',
        cancelLabel: options.cancelLabel || 'Abbrechen',
        onResolve: resolve,
      });
    });
  }, []);

  const handleClose = (_, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar((prev) => {
      if (prev.onResolve) prev.onResolve(false);
      return { ...prev, open: false, onResolve: null };
    });
  };

  return (
    <SnackbarContext.Provider value={{ showSnackbar, confirmSnackbar }}>
      {children}
      <Snackbar
        open={snackbar.open}
        onClose={handleClose}
        autoHideDuration={snackbar.autoHideDuration}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          top: '50% !important',
          left: '50% !important',
          transform: 'translate(-50%, -50%)',
          width: { xs: '92%', sm: 'auto' },
          maxWidth: 520,
        }}
      >
        <Alert
          onClose={handleClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: '100%',
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
            backdropFilter: 'blur(10px)',
            background:
              'linear-gradient(135deg, rgba(15,23,42,0.95), rgba(2,6,23,0.95))',
            border: '1px solid rgba(255,255,255,0.12)',
            animation: 'snackbarPop 180ms ease-out',
            '@keyframes snackbarPop': {
              from: { transform: 'scale(0.96)', opacity: 0 },
              to: { transform: 'scale(1)', opacity: 1 },
            },
          }}
          action={
            snackbar.actionLabel ? (
              <Stack direction="row" spacing={1}>
                <Button
                  size="medium"
                  variant="outlined"
                  color="inherit"
                  sx={{ borderColor: 'rgba(255,255,255,0.6)', px: 2.5, borderRadius: 999 }}
                  onClick={() => {
                    if (snackbar.onResolve) snackbar.onResolve(false);
                    setSnackbar((prev) => ({ ...prev, open: false, onResolve: null }));
                  }}
                >
                  {snackbar.cancelLabel}
                </Button>
                <Button
                  size="medium"
                  variant="contained"
                  color="inherit"
                  sx={{
                    background: 'linear-gradient(90deg, #22c55e, #0ea5e9)',
                    color: '#fff',
                    fontWeight: 700,
                    px: 2.5,
                    borderRadius: 999,
                  }}
                  onClick={() => {
                    if (snackbar.onResolve) snackbar.onResolve(true);
                    setSnackbar((prev) => ({ ...prev, open: false, onResolve: null }));
                  }}
                >
                  {snackbar.actionLabel}
                </Button>
              </Stack>
            ) : null
          }
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
}

export function useSnackbar() {
  return useContext(SnackbarContext);
}
